import { useCallback, useEffect, useMemo, useState } from 'react';
import GiftSetService from '../services/GiftSetService';
import { getReadableGiftSetError } from '../components/giftSet/giftSetUiUtils';

const createOwnerFallback = (ownerUuid) => ({
  uuid: ownerUuid,
  role: 'OWNER',
  nickname: 'Owner NFC',
  scanCount: 0,
  lastScannedAt: null,
});

const DEFAULT_GROUP_NAME = 'Gift Set';

const getEditableGroupName = (name) => {
  const value = String(name || '').trim();
  return value && value !== DEFAULT_GROUP_NAME ? value : '';
};

export const useGiftSetSettings = ({
  feedback,
  isOpen,
  ownerUuid,
}) => {
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);
  const [isPairing, setIsPairing] = useState(false);
  const [pendingBind, setPendingBind] = useState(null);
  const [error, setError] = useState('');

  const ownerMember = useMemo(() => (
    members.find((member) => member.role === 'OWNER') || createOwnerFallback(ownerUuid)
  ), [members, ownerUuid]);

  const memberRows = useMemo(
    () => members.filter((member) => member.role !== 'OWNER'),
    [members],
  );

  const refreshMembers = useCallback(async () => {
    if (!ownerUuid) return;
    setIsLoading(true);
    setError('');
    try {
      const result = await GiftSetService.listGiftSetMembers(ownerUuid);
      setGroup(result.group || null);
      setMembers(result.members || []);
      setGroupName(getEditableGroupName(result.group?.name));
    } catch (err) {
      setGroup(null);
      setMembers([]);
      if (err.code !== 'functions/failed-precondition') {
        setError(getReadableGiftSetError(err));
      }
    } finally {
      setIsLoading(false);
    }
  }, [ownerUuid]);

  useEffect(() => {
    if (!isOpen) return;
    setPendingBind(GiftSetService.getPendingBind());
    refreshMembers();
  }, [isOpen, refreshMembers]);

  const startPairing = useCallback(async () => {
    setIsPairing(true);
    setError('');
    try {
      const session = await GiftSetService.beginAddGiftSetMember(ownerUuid, groupName);
      const pending = GiftSetService.savePendingBind({...session, ownerUuid});
      setPendingBind(pending);
      await refreshMembers();
      feedback.toast({
        type: 'success',
        title: 'Pairing Started',
        message: 'Now scan the NFC you want to add.',
        duration: 5200,
      });
    } catch (err) {
      const message = getReadableGiftSetError(err);
      setError(message);
      await feedback.alert({type: 'error', title: 'Cannot Start Pairing', message});
    } finally {
      setIsPairing(false);
    }
  }, [feedback, groupName, ownerUuid, refreshMembers]);

  const saveName = useCallback(async () => {
    if (!group) {
      await startPairing();
      return;
    }
    setIsSavingName(true);
    setError('');
    try {
      const result = await GiftSetService.updateGiftSetName(ownerUuid, groupName);
      setGroup((current) => ({...(current || {}), ...(result.group || {})}));
      feedback.toast({
        type: 'success',
        title: 'Gift Set Updated',
        message: 'The group name has been saved.',
      });
    } catch (err) {
      const message = getReadableGiftSetError(err);
      setError(message);
      await feedback.alert({type: 'warning', title: 'Name Needs Changing', message});
    } finally {
      setIsSavingName(false);
    }
  }, [feedback, group, groupName, ownerUuid, startPairing]);

  const removeMember = useCallback(async (member) => {
    const confirmed = await feedback.confirm({
      type: 'warning',
      title: 'Remove NFC?',
      message: `${member.nickname || 'This NFC'} will stop opening this Gift Set content.`,
      confirmLabel: 'Remove',
      cancelLabel: 'Keep',
    });
    if (!confirmed) return;

    setIsLoading(true);
    try {
      await GiftSetService.removeGiftSetMember(ownerUuid, member.uuid);
      await refreshMembers();
      feedback.toast({
        type: 'success',
        title: 'NFC Removed',
        message: 'This NFC is available for another Gift Set.',
      });
    } catch (err) {
      await feedback.alert({
        type: 'error',
        title: 'Remove Failed',
        message: getReadableGiftSetError(err),
      });
    } finally {
      setIsLoading(false);
    }
  }, [feedback, ownerUuid, refreshMembers]);

  const addMemberFromUrl = useCallback(async ({memberUrl, nickname}) => {
    const pending = GiftSetService.getPendingBind();
    if (!pending || pending.ownerUuid !== ownerUuid) {
      throw new Error('Start NFC pairing first, then paste or scan the member NFC URL.');
    }

    const memberUuid = GiftSetService.parseUuidFromInput(memberUrl);
    if (!memberUuid) {
      throw new Error('Enter a valid Lumirae NFC URL.');
    }
    if (memberUuid === ownerUuid) {
      throw new Error('The owner NFC is already the master gift.');
    }

    const result = await GiftSetService.completeAddGiftSetMember({
      memberUuid,
      bindSessionToken: pending.token,
      nickname: nickname || `NFC ${GiftSetService.formatShortUuid(memberUuid)}`,
    });
    GiftSetService.markPendingMemberAdded(memberUuid);
    setPendingBind(GiftSetService.getPendingBind());
    await refreshMembers();
    feedback.toast({
      type: 'success',
      title: 'NFC Added',
      message: 'This NFC now opens the owner gift content.',
    });
    return result;
  }, [feedback, ownerUuid, refreshMembers]);

  const stopPairing = useCallback(() => {
    GiftSetService.clearPendingBind();
    setPendingBind(null);
    feedback.toast({
      type: 'info',
      title: 'Pairing Stopped',
      message: 'No more NFCs will be added from this phone.',
    });
  }, [feedback]);

  return {
    error,
    group,
    groupName,
    isLoading,
    isPairing,
    isSavingName,
    memberRows,
    ownerMember,
    pendingBind,
    addMemberFromUrl,
    removeMember,
    saveName,
    setGroupName,
    startPairing,
    stopPairing,
  };
};

export default useGiftSetSettings;
